import { Youtube, ThumbsUp, Share2, Bell, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "../lib/useSeo";
import { VideoCarousel } from "../components/VideoCarousel";

const CHANNEL_URL = "https://www.youtube.com/@AIPromptShortcutCommands";
const SHORTS = ["3pkD4WyXAYg", "aWExI54t_EA", "8LcgU0XxiTE", "evzejRQwbB0"];

export default function PromptQueenTips() {
  useSeo({
    title: "Prompt Queen Tips — AI Advice & Tips",
    description: "Bite-size AI advice and prompt tips from the Prompt Queen. Watch, like and share our latest YouTube Shorts, and follow the channel for new tips.",
  });

  const share = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: "Prompt Queen Tips — AI Advice & Tips", url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => toast.success("Page link copied — share it anywhere"));
    }
  };

  return (
    <div className="bg-[#0B0F0D] min-h-screen" data-testid="prompt-queen-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <img src="/assets/prompt-queen-banner.webp" alt="Prompt Queen — AI Advice & Tips"
          data-testid="prompt-queen-banner" className="w-full max-w-4xl mx-auto" />
        <p className="text-center text-slate-300 mt-6 max-w-2xl mx-auto text-sm sm:text-base">
          Bite-size AI advice and prompt tips — straight from the Prompt Queen. New Shorts drop regularly on our YouTube channel.
        </p>

        <div className="flex flex-wrap justify-center gap-3 mt-7" data-testid="prompt-queen-ctas">
          <a href={CHANNEL_URL} target="_blank" rel="noopener noreferrer" data-testid="pq-follow-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-slate-900 font-bold px-6 py-3 rounded-full hover:brightness-110 transition-all text-sm">
            <Bell size={16} /> Follow the channel
          </a>
          <a href={CHANNEL_URL} target="_blank" rel="noopener noreferrer" data-testid="pq-like-btn"
            className="inline-flex items-center gap-2 border border-emerald-500/60 text-emerald-300 font-bold px-6 py-3 rounded-full hover:bg-emerald-500/10 transition-all text-sm">
            <ThumbsUp size={16} /> Like the Shorts
          </a>
          <button onClick={share} data-testid="pq-share-btn"
            className="inline-flex items-center gap-2 border border-slate-500/60 text-slate-200 font-bold px-6 py-3 rounded-full hover:bg-white/5 transition-all text-sm">
            <Share2 size={16} /> Share this page
          </button>
          <a href="https://www.instagram.com/aipromptshortcuts/" target="_blank" rel="noopener noreferrer" data-testid="pq-instagram-btn"
            className="inline-flex items-center gap-2 border border-pink-500/60 text-pink-300 font-bold px-6 py-3 rounded-full hover:bg-pink-500/10 transition-all text-sm">
            <Instagram size={16} /> Instagram
          </a>
          <a href="https://www.facebook.com/aipromptshortcuts" target="_blank" rel="noopener noreferrer" data-testid="pq-facebook-btn"
            className="inline-flex items-center gap-2 border border-blue-500/60 text-blue-300 font-bold px-6 py-3 rounded-full hover:bg-blue-500/10 transition-all text-sm">
            <Facebook size={16} /> Facebook
          </a>
          <a href="https://www.tiktok.com/@xpressprompts" target="_blank" rel="noopener noreferrer" data-testid="pq-tiktok-btn"
            className="inline-flex items-center gap-2 border border-fuchsia-500/60 text-fuchsia-200 font-bold px-6 py-3 rounded-full hover:bg-fuchsia-500/10 transition-all text-sm">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.57a8.16 8.16 0 0 0 4.77 1.52V7.63a4.85 4.85 0 0 1-1.84-.94z"/>
            </svg>
            TikTok
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12" data-testid="prompt-queen-grid">
          {SHORTS.map((id) => (
            <div key={id} className="rounded-2xl overflow-hidden border border-emerald-900/60 bg-black shadow-[0_0_24px_rgba(16,185,129,0.12)]" data-testid={`pq-short-${id}`}>
              <div className="relative w-full" style={{ paddingBottom: "177.78%" }}>
                <iframe
                  src={`https://www.youtube.com/embed/${id}`}
                  title="Prompt Queen Tips — YouTube Short"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12" data-testid="prompt-queen-carousel">
          <VideoCarousel page="prompt-queen-tips" />
        </div>

        <div className="text-center mt-12">
          <a href={CHANNEL_URL} target="_blank" rel="noopener noreferrer" data-testid="pq-channel-link"
            className="inline-flex items-center gap-2 text-[#D4AF37] font-bold hover:underline">
            <Youtube size={18} /> More tips on our YouTube channel — follow, like &amp; share
          </a>
        </div>
      </div>
    </div>
  );
}
